import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  AlertCircle,
  Plus,
  X,
  Briefcase,
  Target,
  DollarSign,
  GraduationCap,
  UserCheck,
  Globe,
  FileText,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { useCreateCustomer } from '@/lib/hooks/useCustomers';
import type { CreateCustomerData } from '@/lib/api/customers';
import { LocationSelect } from '@/components/customers/LocationSelect';

export const Route = createFileRoute('/admin/customers/new')({
  component: NewCustomerPage,
});

function NewCustomerPage() {
  const navigate = useNavigate();
  const createCustomerMutation = useCreateCustomer();
  const [submitError, setSubmitError] = useState<string>();
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState<CreateCustomerData>({
    name: '',
    email: '',
    phone: '',
    password: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateCustomerData, string>>>({});
  const [showPassword, setShowPassword] = useState(false);

  // Tab order
  const tabs = ['basic', 'professional', 'preferences', 'compensation', 'education', 'other'];
  const currentTabIndex = tabs.indexOf(activeTab);
  const isLastTab = currentTabIndex === tabs.length - 1;
  const isFirstTab = currentTabIndex === 0;

  // Validate current tab before proceeding
  const validateCurrentTab = (): boolean => {
    const newErrors: Partial<Record<keyof CreateCustomerData, string>> = {};

    if (activeTab === 'basic') {
      if (!formData.name.trim()) {
        newErrors.name = 'Name is required';
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Name must be at least 2 characters';
      } else if (formData.name.trim().length > 255) {
        newErrors.name = 'Name must not exceed 255 characters';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email must be a valid email address';
      }

      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone is required';
      } else if (formData.phone.trim().length < 10) {
        newErrors.phone = 'Phone must be at least 10 characters';
      } else if (formData.phone.trim().length > 50) {
        newErrors.phone = 'Phone must not exceed 50 characters';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }

    if (activeTab === 'professional') {
      if (!formData.years_of_experience && formData.years_of_experience !== 0) {
        newErrors.years_of_experience = 'Years of experience is required';
      }
      if (!formData.career_level) {
        newErrors.career_level = 'Career level is required';
      }
    }

    if (activeTab === 'preferences') {
      if (!formData.preferred_job_titles || formData.preferred_job_titles.length === 0) {
        newErrors.preferred_job_titles = 'At least one preferred job title is required';
      }
      if (!formData.preferred_locations || formData.preferred_locations.length === 0) {
        newErrors.preferred_locations = 'At least one preferred location is required';
      }
      if (!formData.preferred_work_modes || formData.preferred_work_modes.length === 0) {
        newErrors.preferred_work_modes = 'At least one preferred work mode is required';
      }
    }

    if (activeTab === 'compensation') {
      if (formData.expected_salary_min !== undefined && formData.expected_salary_max !== undefined) {
        if (formData.expected_salary_min > formData.expected_salary_max) {
          newErrors.expected_salary_max = 'Maximum salary must be greater than minimum salary';
        }
      }
    }

    if (activeTab === 'other') {
      if (!formData.work_authorization) {
        newErrors.work_authorization = 'Work authorization is required';
      }
      if (!formData.availability_status) {
        newErrors.availability_status = 'Availability status is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentTab()) {
      return;
    }
    if (!isLastTab) {
      setActiveTab(tabs[currentTabIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (!isFirstTab) {
      setActiveTab(tabs[currentTabIndex - 1]);
    }
  };

  // Helper functions for array fields
  const addArrayItem = (field: keyof CreateCustomerData, item: string) => {
    const current = (formData[field] as string[]) || [];
    setFormData({ ...formData, [field]: [...current, item] });
  };

  const removeArrayItem = (field: keyof CreateCustomerData, index: number) => {
    const current = (formData[field] as string[]) || [];
    setFormData({ ...formData, [field]: current.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(undefined);

    // Final validation before submit
    if (!validateCurrentTab()) {
      return;
    }

    try {
      // Clean up empty arrays and undefined values
      const cleanedData: CreateCustomerData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        country: formData.country?.trim() || undefined,
        state: formData.state?.trim() || undefined,
        city: formData.city?.trim() || undefined,
        pincode: formData.pincode?.trim() || undefined,
        street_address: formData.street_address?.trim() || undefined,
        years_of_experience: formData.years_of_experience || undefined,
        current_job_title: formData.current_job_title?.trim() || undefined,
        current_company: formData.current_company?.trim() || undefined,
        current_industry: formData.current_industry?.trim() || undefined,
        career_level: formData.career_level || undefined,
        employment_history: formData.employment_history && formData.employment_history.length > 0 ? formData.employment_history : undefined,
        preferred_job_titles: formData.preferred_job_titles && formData.preferred_job_titles.length > 0 ? formData.preferred_job_titles : undefined,
        preferred_locations: formData.preferred_locations && formData.preferred_locations.length > 0 ? formData.preferred_locations : undefined,
        preferred_categories: formData.preferred_categories && formData.preferred_categories.length > 0 ? formData.preferred_categories : undefined,
        preferred_industries: formData.preferred_industries && formData.preferred_industries.length > 0 ? formData.preferred_industries : undefined,
        preferred_work_modes: formData.preferred_work_modes && formData.preferred_work_modes.length > 0 ? formData.preferred_work_modes : undefined,
        preferred_job_types: formData.preferred_job_types && formData.preferred_job_types.length > 0 ? formData.preferred_job_types : undefined,
        relocation_willing: formData.relocation_willing || undefined,
        remote_only: formData.remote_only || undefined,
        expected_salary_min: formData.expected_salary_min || undefined,
        expected_salary_max: formData.expected_salary_max || undefined,
        salary_currency: formData.salary_currency || undefined,
        salary_negotiable: formData.salary_negotiable || undefined,
        benefits_preferences: formData.benefits_preferences || undefined,
        education_history: formData.education_history && formData.education_history.length > 0 ? formData.education_history : undefined,
        highest_education_level: formData.highest_education_level || undefined,
        certifications: formData.certifications && formData.certifications.length > 0 ? formData.certifications : undefined,
        languages: formData.languages && formData.languages.length > 0 ? formData.languages : undefined,
        skills_proficiency: formData.skills_proficiency && Object.keys(formData.skills_proficiency).length > 0 ? formData.skills_proficiency : undefined,
        work_authorization: formData.work_authorization || undefined,
        requires_sponsorship: formData.requires_sponsorship || undefined,
        notice_period_days: formData.notice_period_days || undefined,
        available_start_date: formData.available_start_date || undefined,
        availability_status: formData.availability_status || undefined,
        portfolio_url: formData.portfolio_url?.trim() || undefined,
        linkedin_url: formData.linkedin_url?.trim() || undefined,
        github_url: formData.github_url?.trim() || undefined,
        personal_website: formData.personal_website?.trim() || undefined,
        projects: formData.projects && formData.projects.length > 0 ? formData.projects : undefined,
        career_objectives: formData.career_objectives?.trim() || undefined,
        target_companies: formData.target_companies && formData.target_companies.length > 0 ? formData.target_companies : undefined,
        deal_breakers: formData.deal_breakers && formData.deal_breakers.length > 0 ? formData.deal_breakers : undefined,
        must_haves: formData.must_haves && formData.must_haves.length > 0 ? formData.must_haves : undefined,
        profile_visibility: formData.profile_visibility || undefined,
        job_search_status: formData.job_search_status || undefined,
      };

      await createCustomerMutation.mutateAsync(cleanedData);
      navigate({ to: '/admin/customers' });
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || error.message || 'Failed to create customer. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate({ to: '/admin/customers' });
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/customers">Customers</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Add New Customer</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Add New Customer</h1>
          <p className="text-sm text-muted-foreground">
            Create a comprehensive customer profile. All fields marked with * are required.
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {/* Form with Tabs */}
      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="professional">Professional</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="compensation">Compensation</TabsTrigger>
            <TabsTrigger value="education">Education</TabsTrigger>
            <TabsTrigger value="other">Other</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Basic Information
                </CardTitle>
                <CardDescription>
                  Essential information required to create a customer account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={errors.name ? 'border-destructive' : ''}
                      disabled={createCustomerMutation.isPending}
                    />
                    {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={errors.email ? 'border-destructive' : ''}
                      disabled={createCustomerMutation.isPending}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className={errors.phone ? 'border-destructive' : ''}
                      disabled={createCustomerMutation.isPending}
                    />
                    {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                  </div>

                  {/* Address (Country / State / City) */}
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address</Label>
                    <LocationSelect
                      country={formData.country || ''}
                      state={formData.state || ''}
                      city={formData.city || ''}
                      onCountryChange={(value) => setFormData({ ...formData, country: value || undefined })}
                      onStateChange={(value) => setFormData({ ...formData, state: value || undefined })}
                      onCityChange={(value) => setFormData({ ...formData, city: value || undefined })}
                      errors={{ country: errors.country, state: errors.state, city: errors.city }}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Pincode */}
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode</Label>
                    <Input
                      id="pincode"
                      placeholder="e.g., 400001"
                      value={formData.pincode || ''}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Street Address */}
                  <div className="space-y-2">
                    <Label htmlFor="street_address">Street Address</Label>
                    <Input
                      id="street_address"
                      placeholder="123 Main St, Apt 4B"
                      value={formData.street_address || ''}
                      onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="password">
                      Password <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                        disabled={createCustomerMutation.isPending}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={createCustomerMutation.isPending}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="sr-only">{showPassword ? 'Hide password' : 'Show password'}</span>
                      </Button>
                    </div>
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                    <p className="text-xs text-muted-foreground">
                      Password must be at least 8 characters long
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Professional Background Tab */}
          <TabsContent value="professional" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Professional Background
                </CardTitle>
                <CardDescription>
                  Current position, experience, and career information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Years of Experience */}
                  <div className="space-y-2">
                    <Label htmlFor="years_of_experience">
                      Years of Experience <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="years_of_experience"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="5"
                      value={formData.years_of_experience || ''}
                      onChange={(e) => setFormData({ ...formData, years_of_experience: e.target.value ? parseInt(e.target.value) : undefined })}
                      className={errors.years_of_experience ? 'border-destructive' : ''}
                      disabled={createCustomerMutation.isPending}
                    />
                    {errors.years_of_experience && <p className="text-sm text-destructive">{errors.years_of_experience}</p>}
                  </div>

                  {/* Career Level */}
                  <div className="space-y-2">
                    <Label htmlFor="career_level">
                      Career Level <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.career_level || ''}
                      onValueChange={(value) => setFormData({ ...formData, career_level: value as any || undefined })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select career level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entry">Entry Level</SelectItem>
                        <SelectItem value="mid">Mid Level</SelectItem>
                        <SelectItem value="senior">Senior Level</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="executive">Executive</SelectItem>
                        <SelectItem value="c_level">C-Level</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.career_level && <p className="text-sm text-destructive">{errors.career_level}</p>}
                  </div>

                  {/* Current Job Title */}
                  <div className="space-y-2">
                    <Label htmlFor="current_job_title">Current Job Title</Label>
                    <Input
                      id="current_job_title"
                      placeholder="Software Engineer"
                      value={formData.current_job_title || ''}
                      onChange={(e) => setFormData({ ...formData, current_job_title: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Current Company */}
                  <div className="space-y-2">
                    <Label htmlFor="current_company">Current Company</Label>
                    <Input
                      id="current_company"
                      placeholder="Tech Corp"
                      value={formData.current_company || ''}
                      onChange={(e) => setFormData({ ...formData, current_company: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Current Industry */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="current_industry">Current Industry</Label>
                    <Input
                      id="current_industry"
                      placeholder="Technology"
                      value={formData.current_industry || ''}
                      onChange={(e) => setFormData({ ...formData, current_industry: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Job Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Job Preferences
                </CardTitle>
                <CardDescription>
                  Preferred job types, locations, and work arrangements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Preferred Job Titles */}
                <div className="space-y-2">
                  <Label>
                    Preferred Job Titles <span className="text-destructive">*</span>
                  </Label>
                  <div className="space-y-2">
                    {(formData.preferred_job_titles || []).map((title, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={title}
                          onChange={(e) => {
                            const updated = [...(formData.preferred_job_titles || [])];
                            updated[idx] = e.target.value;
                            setFormData({ ...formData, preferred_job_titles: updated });
                          }}
                          placeholder="e.g., Software Engineer"
                          disabled={createCustomerMutation.isPending}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayItem('preferred_job_titles', idx)}
                          disabled={createCustomerMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('preferred_job_titles', '')}
                      disabled={createCustomerMutation.isPending}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Job Title
                    </Button>
                    {errors.preferred_job_titles && <p className="text-sm text-destructive">{errors.preferred_job_titles}</p>}
                  </div>
                </div>

                {/* Preferred Locations */}
                <div className="space-y-2">
                  <Label>
                    Preferred Locations <span className="text-destructive">*</span>
                  </Label>
                  <div className="space-y-2">
                    {(formData.preferred_locations || []).map((location, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={location}
                          onChange={(e) => {
                            const updated = [...(formData.preferred_locations || [])];
                            updated[idx] = e.target.value;
                            setFormData({ ...formData, preferred_locations: updated });
                          }}
                          placeholder="e.g., San Francisco, CA"
                          disabled={createCustomerMutation.isPending}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayItem('preferred_locations', idx)}
                          disabled={createCustomerMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('preferred_locations', '')}
                      disabled={createCustomerMutation.isPending}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Location
                    </Button>
                    {errors.preferred_locations && <p className="text-sm text-destructive">{errors.preferred_locations}</p>}
                  </div>
                </div>

                {/* Preferred Work Modes */}
                <div className="space-y-2">
                  <Label>
                    Preferred Work Modes <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex flex-wrap gap-4">
                    {['remote', 'hybrid', 'onsite'].map((mode) => (
                      <div key={mode} className="flex items-center space-x-2">
                        <Checkbox
                          id={`work_mode_${mode}`}
                          checked={(formData.preferred_work_modes || []).includes(mode as any)}
                          onCheckedChange={(checked) => {
                            const current = formData.preferred_work_modes || [];
                            if (checked) {
                              setFormData({ ...formData, preferred_work_modes: [...current, mode as any] });
                            } else {
                              setFormData({ ...formData, preferred_work_modes: current.filter((m) => m !== mode) });
                            }
                          }}
                          disabled={createCustomerMutation.isPending}
                        />
                        <Label htmlFor={`work_mode_${mode}`} className="capitalize cursor-pointer">
                          {mode}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preferred Job Types */}
                <div className="space-y-2">
                  <Label>Preferred Job Types</Label>
                  <div className="flex flex-wrap gap-4">
                    {['full-time', 'part-time', 'contract', 'temporary'].map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`job_type_${type}`}
                          checked={(formData.preferred_job_types || []).includes(type as any)}
                          onCheckedChange={(checked) => {
                            const current = formData.preferred_job_types || [];
                            if (checked) {
                              setFormData({ ...formData, preferred_job_types: [...current, type as any] });
                            } else {
                              setFormData({ ...formData, preferred_job_types: current.filter((t) => t !== type) });
                            }
                          }}
                          disabled={createCustomerMutation.isPending}
                        />
                        <Label htmlFor={`job_type_${type}`} className="capitalize cursor-pointer">
                          {type.replace('-', ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Relocation & Remote Options */}
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="relocation_willing"
                      checked={formData.relocation_willing || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, relocation_willing: checked as boolean })}
                      disabled={createCustomerMutation.isPending}
                    />
                    <Label htmlFor="relocation_willing" className="cursor-pointer">
                      Willing to relocate
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remote_only"
                      checked={formData.remote_only || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, remote_only: checked as boolean })}
                      disabled={createCustomerMutation.isPending}
                    />
                    <Label htmlFor="remote_only" className="cursor-pointer">
                      Remote only
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compensation Tab */}
          <TabsContent value="compensation" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Compensation Expectations
                </CardTitle>
                <CardDescription>
                  Salary expectations and compensation preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Expected Salary Min */}
                  <div className="space-y-2">
                    <Label htmlFor="expected_salary_min">Expected Salary (Min)</Label>
                    <Input
                      id="expected_salary_min"
                      type="number"
                      min="0"
                      placeholder="50000"
                      value={formData.expected_salary_min || ''}
                      onChange={(e) => setFormData({ ...formData, expected_salary_min: e.target.value ? parseFloat(e.target.value) : undefined })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Expected Salary Max */}
                  <div className="space-y-2">
                    <Label htmlFor="expected_salary_max">Expected Salary (Max)</Label>
                    <Input
                      id="expected_salary_max"
                      type="number"
                      min="0"
                      placeholder="100000"
                      value={formData.expected_salary_max || ''}
                      onChange={(e) => setFormData({ ...formData, expected_salary_max: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className={errors.expected_salary_max ? 'border-destructive' : ''}
                      disabled={createCustomerMutation.isPending}
                    />
                    {errors.expected_salary_max && <p className="text-sm text-destructive">{errors.expected_salary_max}</p>}
                  </div>

                  {/* Salary Currency */}
                  <div className="space-y-2">
                    <Label htmlFor="salary_currency">Salary Currency</Label>
                    <Select
                      value={formData.salary_currency || 'INR'}
                      onValueChange={(value) => setFormData({ ...formData, salary_currency: value })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Salary Negotiable */}
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="salary_negotiable"
                        checked={formData.salary_negotiable ?? true}
                        onCheckedChange={(checked) => setFormData({ ...formData, salary_negotiable: checked as boolean })}
                        disabled={createCustomerMutation.isPending}
                      />
                      <Label htmlFor="salary_negotiable" className="cursor-pointer">
                        Salary is negotiable
                      </Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Education & Skills Tab */}
          <TabsContent value="education" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Education & Skills
                </CardTitle>
                <CardDescription>
                  Educational background, certifications, and skills
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Highest Education Level */}
                  <div className="space-y-2">
                    <Label htmlFor="highest_education_level">Highest Education Level</Label>
                    <Select
                      value={formData.highest_education_level || ''}
                      onValueChange={(value) => setFormData({ ...formData, highest_education_level: value as any || undefined })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select education level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high_school">High School</SelectItem>
                        <SelectItem value="associate">Associate Degree</SelectItem>
                        <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                        <SelectItem value="master">Master's Degree</SelectItem>
                        <SelectItem value="phd">PhD</SelectItem>
                        <SelectItem value="professional">Professional Certification</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Other Information Tab */}
          <TabsContent value="other" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Work Authorization & Availability
                </CardTitle>
                <CardDescription>
                  Work authorization status and availability information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Work Authorization */}
                  <div className="space-y-2">
                    <Label htmlFor="work_authorization">
                      Work Authorization <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.work_authorization || ''}
                      onValueChange={(value) => setFormData({ ...formData, work_authorization: value as any || undefined })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select work authorization" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="citizen">Citizen</SelectItem>
                        <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                        <SelectItem value="work_visa">Work Visa</SelectItem>
                        <SelectItem value="requires_sponsorship">Requires Sponsorship</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.work_authorization && <p className="text-sm text-destructive">{errors.work_authorization}</p>}
                  </div>

                  {/* Availability Status */}
                  <div className="space-y-2">
                    <Label htmlFor="availability_status">
                      Availability Status <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.availability_status || ''}
                      onValueChange={(value) => setFormData({ ...formData, availability_status: value as any || undefined })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select availability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediately">Immediately</SelectItem>
                        <SelectItem value="within_2_weeks">Within 2 Weeks</SelectItem>
                        <SelectItem value="within_month">Within a Month</SelectItem>
                        <SelectItem value="within_3_months">Within 3 Months</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.availability_status && <p className="text-sm text-destructive">{errors.availability_status}</p>}
                  </div>

                  {/* Notice Period Days */}
                  <div className="space-y-2">
                    <Label htmlFor="notice_period_days">Notice Period (Days)</Label>
                    <Input
                      id="notice_period_days"
                      type="number"
                      min="0"
                      max="365"
                      placeholder="30"
                      value={formData.notice_period_days || ''}
                      onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value ? parseInt(e.target.value) : undefined })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Available Start Date */}
                  <div className="space-y-2">
                    <Label htmlFor="available_start_date">Available Start Date</Label>
                    <Input
                      id="available_start_date"
                      type="date"
                      value={formData.available_start_date || ''}
                      onChange={(e) => setFormData({ ...formData, available_start_date: e.target.value || undefined })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Requires Sponsorship */}
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="requires_sponsorship"
                        checked={formData.requires_sponsorship || false}
                        onCheckedChange={(checked) => setFormData({ ...formData, requires_sponsorship: checked as boolean })}
                        disabled={createCustomerMutation.isPending}
                      />
                      <Label htmlFor="requires_sponsorship" className="cursor-pointer">
                        Requires sponsorship
                      </Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Portfolio & Online Presence */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Portfolio & Online Presence
                </CardTitle>
                <CardDescription>
                  Links to professional profiles and portfolios
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* LinkedIn URL */}
                  <div className="space-y-2">
                    <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                    <Input
                      id="linkedin_url"
                      type="url"
                      placeholder="https://linkedin.com/in/johndoe"
                      value={formData.linkedin_url || ''}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* GitHub URL */}
                  <div className="space-y-2">
                    <Label htmlFor="github_url">GitHub URL</Label>
                    <Input
                      id="github_url"
                      type="url"
                      placeholder="https://github.com/johndoe"
                      value={formData.github_url || ''}
                      onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Portfolio URL */}
                  <div className="space-y-2">
                    <Label htmlFor="portfolio_url">Portfolio URL</Label>
                    <Input
                      id="portfolio_url"
                      type="url"
                      placeholder="https://johndoe.dev"
                      value={formData.portfolio_url || ''}
                      onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>

                  {/* Personal Website */}
                  <div className="space-y-2">
                    <Label htmlFor="personal_website">Personal Website</Label>
                    <Input
                      id="personal_website"
                      type="url"
                      placeholder="https://johndoe.com"
                      value={formData.personal_website || ''}
                      onChange={(e) => setFormData({ ...formData, personal_website: e.target.value })}
                      disabled={createCustomerMutation.isPending}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profile Metadata */}
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>
                  Profile visibility and job search status
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Job Search Status */}
                  <div className="space-y-2">
                    <Label htmlFor="job_search_status">Job Search Status</Label>
                    <Select
                      value={formData.job_search_status || 'actively_looking'}
                      onValueChange={(value) => setFormData({ ...formData, job_search_status: value as any })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="actively_looking">Actively Looking</SelectItem>
                        <SelectItem value="passively_looking">Passively Looking</SelectItem>
                        <SelectItem value="not_looking">Not Looking</SelectItem>
                        <SelectItem value="hired">Hired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Profile Visibility */}
                  <div className="space-y-2">
                    <Label htmlFor="profile_visibility">Profile Visibility</Label>
                    <Select
                      value={formData.profile_visibility || 'public'}
                      onValueChange={(value) => setFormData({ ...formData, profile_visibility: value })}
                      disabled={createCustomerMutation.isPending}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="recruiters_only">Recruiters Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions - Fixed at bottom */}
        <div className="flex items-center justify-between gap-4 pt-6 border-t sticky bottom-0 bg-background pb-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={createCustomerMutation.isPending}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-2">
            {!isFirstTab && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={createCustomerMutation.isPending}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
            )}
            {!isLastTab ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={createCustomerMutation.isPending}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button type="submit" disabled={createCustomerMutation.isPending}>
                {createCustomerMutation.isPending ? 'Creating...' : 'Create Customer'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
